import * as React from 'react';
import { cn } from '@/utils/utils';
import { EyeIcon, EyeOffIcon, Dot, X } from 'lucide-react';
import { OTPInput, OTPInputContext } from 'input-otp';
import { Label } from './Label';
import { MAX_LENGTH_DEFAULT_INPUT, MAX_LENGTH_NUMBER } from '@/configs/constants';
import { convertFullToHalf } from '@/utils/formatNumber';

// #region Input
export interface InputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    isPreventCopy?: boolean,
    isPreventCut?: boolean,
    isTypingOnlyNumber?: boolean,
    // isPhoneNumber?: boolean,
    isHalfWidth?: boolean,
    useMaxLengthDefault?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({
        className,
        maxLength = MAX_LENGTH_DEFAULT_INPUT,
        type,
        isPreventCopy,
        isPreventCut,
        isTypingOnlyNumber,
        isHalfWidth,
        useMaxLengthDefault,
        onCopy,
        onChange,
        value,
        ...props
    }, ref) => {
        const [composingValue, setComposingValue] = React.useState('');
        const isComposingRef = React.useRef(false);
        const inputRef = React.useRef<HTMLInputElement>(null);

        // Kết hợp ref từ bên ngoài và ref nội bộ
        React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        const onTypingValidation = (e: React.KeyboardEvent<HTMLInputElement>, maxLength: number) => {
            if (isComposingRef.current) return;

            const key = e.key;
            const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'];
            const isCtrlKey = (e.metaKey || e.ctrlKey) && ['a', 'c', 'v', 'x'].includes(key);

            if (allowedKeys.includes(key) || isCtrlKey) return;

            if (isTypingOnlyNumber) {
                const isNumber = /^[0-9]$/.test(key);
                if ((inputRef.current?.value.length || 0) >= maxLength || !isNumber) {
                    e.preventDefault();
                }
            }
        };

        const onPreventCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault();
        };

        const onPreventCut = (e: React.ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault();
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            if (isHalfWidth) {
                const finalValue = isComposingRef.current ? composingValue : e.target.value;
                const halfWidth = convertFullToHalf(finalValue);

                if (halfWidth !== finalValue) {
                    const event = {
                        ...e,
                        target: {
                            ...e.target,
                            value: halfWidth
                        }
                    };
                    onChange?.(event as React.ChangeEvent<HTMLInputElement>);
                    e.target.value = halfWidth;
                }
            }

            // Reset composition state
            isComposingRef.current = false;
            setComposingValue('');
        };

        const handleCompositionStart = (e: React.CompositionEvent<HTMLInputElement>) => {
            isComposingRef.current = true;
            // setComposingValue((value || '') + e.data);
        };

        const handleCompositionUpdate = (e: React.CompositionEvent<HTMLInputElement>) => {
            // setComposingValue((value || '') + e.data);
        };

        const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
            const finalValue = composingValue;
            isComposingRef.current = false;
            setComposingValue('');

            // Gửi sự kiện onChange với giá trị hoàn chỉnh
            const event = {
                ...e,
                target: {
                    ...inputRef.current!,
                    value: finalValue
                }
            };
            onChange?.(event as React.ChangeEvent<HTMLInputElement>);
        };

        // Xử lý onChange cho cả composition và non-composition
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (isComposingRef.current) {
                // Cập nhật giá trị tạm thời trong composition
                setComposingValue(e.target.value);
            } else {
                // Gửi sự kiện onChange bình thường khi không trong composition
                onChange?.(e);
            }
        };

        // Tính giá trị hiển thị
        const displayValue = isComposingRef.current ? composingValue : value;

        return (
            <input
                type={type}
                value={displayValue}
                maxLength={
                    useMaxLengthDefault ?
                        MAX_LENGTH_DEFAULT_INPUT :
                        (isTypingOnlyNumber ? MAX_LENGTH_NUMBER : maxLength)
                }
                className={cn(
                    'text-sm text-text flex h-12 w-full rounded-base border border-border-input bg-background-input px-3 py-2 ring-offset-background transition-colors duration-300 placeholder:text-placeholder',
                    'focus-visible:outline-0 focus-visible:border-primaryWeb',
                    'disabled:cursor-not-allowed disabled:!bg-disable disabled:hover:border-disable',
                    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                    'hover:border-primaryWeb',
                    className
                )}
                ref={inputRef}
                onKeyDown={isTypingOnlyNumber ? e => onTypingValidation(e, maxLength) : undefined}
                onCopy={isPreventCopy ? onPreventCopy : onCopy}
                onCut={isPreventCut ? onPreventCut : undefined}
                onCompositionStart={handleCompositionStart}
                onCompositionUpdate={handleCompositionUpdate}
                onCompositionEnd={handleCompositionEnd}
                onChange={handleChange}
                {...props}
                onBlur={handleBlur}
            />
        );
    }
);
Input.displayName = 'Input';

// #endregion

// #region Input password
export interface PasswordInputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    onToggleIcon?: () => void
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
    ({ type, onToggleIcon, className, autoComplete, ...props }, ref) => {
        return (
            <div className='relative'>
                <Input ref={ref} type={type} className={cn('pr-10', className)} autoComplete={autoComplete} {...props} />
                {
                    type === 'password' ?
                        <EyeOffIcon className='icon-right-input' onClick={onToggleIcon} /> :
                        <EyeIcon className='icon-right-input' onClick={onToggleIcon} />
                }
            </div>
        );
    }
);
PasswordInput.displayName = 'PasswordInput';

// #endregion

// #region Input with icon
export interface InputWithClearProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    icon?: React.ReactNode,
    clear?: boolean
}

const InputWithClear = React.forwardRef<HTMLInputElement, InputWithClearProps>(
    ({ type, className, clear, ...props }, ref) => {
        const onClearInput = () => {
            if (props.onChange) {
                props.onChange({
                    ...{} as React.ChangeEvent<HTMLInputElement>,
                    target: { value: '' } as EventTarget & HTMLInputElement
                });
            }
        };

        return (
            <div className='relative'>
                <Input
                    ref={ref}
                    type={type}
                    className={cn(clear && props.value && 'pr-10', className)}
                    {...props}
                />
                {clear && props.value && (
                    <X className='icon-right-input' onClick={onClearInput} />
                )}
            </div>
        );
    }
);
InputWithClear.displayName = 'InputWithClear';

// #endregion

// #region Input OTP
const InputOTP = React.forwardRef<
    React.ElementRef<typeof OTPInput>,
    React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
    <OTPInput
        ref={ref}
        containerClassName={cn(
            'flex items-center gap-2 has-[:disabled]:opacity-50',
            containerClassName
        )}
        className={cn('disabled:cursor-not-allowed', className)}
        {...props}
    />
));
InputOTP.displayName = 'InputOTP';

const InputOTPGroup = React.forwardRef<
    React.ElementRef<'div'>,
    React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center', className)} {...props} />
));
InputOTPGroup.displayName = 'InputOTPGroup';

const InputOTPSlot = React.forwardRef<
    React.ElementRef<'div'>,
    React.ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, className, ...props }, ref) => {
    const inputOTPContext = React.useContext(OTPInputContext);
    const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];

    return (
        <div
            ref={ref}
            className={cn(
                'relative flex h-10 w-10 items-center justify-center border-y border-r border-input text-sm transition-all first:rounded-l-md first:border-l last:rounded-r-md',
                isActive && 'z-10 border border-primary',
                className
            )}
            {...props}
        >
            {char}
            {hasFakeCaret && (
                <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
                    <div className='h-4 w-px animate-caret-blink bg-foreground duration-1000' />
                </div>
            )}
        </div>
    );
});
InputOTPSlot.displayName = 'InputOTPSlot';

const InputOTPSeparator = React.forwardRef<
    React.ElementRef<'div'>,
    React.ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
    <div ref={ref} role='separator' {...props}>
        <Dot />
    </div>
));
InputOTPSeparator.displayName = 'InputOTPSeparator';

// #endregion

// #region Input floating label

const FloatingInput = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className, ...props }, ref) => {
        return <Input placeholder=' ' className={cn('peer h-[3.125rem] pt-6 px-3 pb-2 rounded-[0.625rem]', className)} ref={ref} {...props} />;
    }
);
FloatingInput.displayName = 'FloatingInput';

const FloatingLabel = React.forwardRef<
    React.ElementRef<typeof Label>,
    React.ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => {
    return (
        <Label
            className={cn(
                'leading-4 absolute start-3 top-2 z-10 origin-left scale-75 text-gray-500 duration-300',
                'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100',
                'peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:scale-75',
                className
            )}
            ref={ref}
            {...props}
        />
    );
});
FloatingLabel.displayName = 'FloatingLabel';

type FloatingLabelInputProps = InputProps & {
    label?: string,
    icon?: React.ReactNode,
    required?: boolean
};

const FloatingLabelInput = React.forwardRef<
    React.ElementRef<typeof FloatingInput>,
    React.PropsWithoutRef<FloatingLabelInputProps>
>(({ id, label, icon, className, required, ...props }, ref) => {
    return (
        <div className='relative detect-autofill'>
            <FloatingInput
                ref={ref}
                id={id}
                className={cn(icon && 'pr-10', className)}
                {...props}
            />
            <FloatingLabel htmlFor={id} required={required}>{label}</FloatingLabel>
            {icon}
        </div>
    );
});
FloatingLabelInput.displayName = 'FloatingLabelInput';

const FloatingLabelDropdown = React.forwardRef<
    HTMLSelectElement,
    {
        id: string,
        label: string,
        options: string[],
        className?: string,
        required?: boolean
    }
>(({ id, label, options, className, required, ...props }, ref) => {
    return (
        <div className='relative detect-autofill'>
            <select
                id={id}
                ref={ref}
                className={cn(
                    'peer h-[3.125rem] pt-6 px-3 pb-2 rounded-[0.625rem] border border-gray-300 focus:border-gray-500 w-full appearance-none bg-white',
                    className
                )}
                {...props}
            >
                {options.map((option, index) => (
                    <option key={index} value={option}>
                        {option}
                    </option>
                ))}
            </select>
            <FloatingLabel htmlFor={id} required={required}>
                {label}
            </FloatingLabel>
        </div>
    );
});
FloatingLabelDropdown.displayName = 'FloatingLabelDropdown';

const FloatingLabelTimeInput = React.forwardRef<
    HTMLInputElement,
    {
        id: string,
        label: string,
        className?: string,
        required?: boolean
    }
>(({ id, label, className, required, ...props }, ref) => {
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const isValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(value); // Regex kiểm tra định dạng 24h
        if (!isValid) {
            e.target.value = ''; // Xóa nếu không hợp lệ
        }
    };
    return (
        <div className='relative detect-autofill'>
            <input
                id={id}
                type='time'
                ref={ref}
                className={cn(
                    'peer h-[3.125rem] pt-6 px-3 pb-2 rounded-[0.625rem] border border-gray-300 focus:border-gray-500 w-full appearance-none bg-white',
                    className
                )}
                step='60'
                onInput={handleInput}
                {...props}
            />
            <FloatingLabel htmlFor={id} required={required}>
                {label}
            </FloatingLabel>
        </div>
    );
});
FloatingLabelTimeInput.displayName = 'FloatingLabelTimeInput';

// #endregion

export {
    Input,
    InputWithClear,
    PasswordInput,
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
    InputOTPSeparator,
    FloatingLabelInput,
    FloatingLabelDropdown,
    FloatingLabelTimeInput
};
